import { Box, Flex, LoadingOverlay, Table as Mantinetable } from "@mantine/core"
import { useEffect,
  forwardRef, ForwardRefRenderFunction,
  useCallback, useImperativeHandle, useMemo, useRef, useState,
} from "react"
import { useNavigate } from "react-router"
import { useSelector } from "react-redux"
import { useTranslation } from "react-i18next"
import { OrderApi, SplitApi } from "../../../apis"
import { SalesAction, useSalesActions } from "../../../components/order/order-actions"
import { usePermission } from "../../../components/permission"
import { Sort } from "../../../components/table"
import { CustomsTable } from "../../../components/custom-table"
import { ITableRef } from "../../../components/custom-table"
import { IOrderPlus } from "../../../models/Order"
import { IGeneral } from "../../../store/General"
import HttpHandler from "../../../utils/http-handler"
import { message } from "../../../utils/message"
import { DEFAULT_COLUMN_IDS, SALES_COLUMNS, SPLIT_COLUMN_IDS } from "./columns"
import { SupplierSalesActionModals } from "./SupplierSalesActionModals"
import { SupplierSalesFilterBar } from "./SupplierSalesFilterBar"
import { useHelpContext, EmptyStateGuide } from "@dehub/theme"
import { SupplierSalesTableHead } from "./SupplierSalesTableHead"
import { SupplierSalesTableRow } from "./SupplierSalesTableRow"
import {
  ActionState, ConsignmentFilter, RowActionKey,
  SupplierSalesListProps, SupplierSalesListRef,
} from "./types"
import { useSupplierSalesFilters } from "./useSupplierSalesFilters"

const isSelectable = (row: IOrderPlus) =>
  row?.orderStatus === "AUTHORIZED" && row?.deliveryNote === null

const Component: ForwardRefRenderFunction<SupplierSalesListRef, SupplierSalesListProps> = (
  { setChecked, actions, checked, setActions, onCreate, mode = "sales", consignmentFilter: consignmentFilterProp, setConsignmentFilter: setConsignmentFilterProp },
  ref,
) => {
  const { t } = useTranslation(["supplierSales", "common"])
  const navigate = useNavigate()
  const tableRef = useRef<ITableRef>(null)

  const [currentRows, setCurrentRows] = useState<IOrderPlus[]>([])
  const [action, setAction] = useState<ActionState>({ kind: "none" })
  const [internalConsignmentFilter, setInternalConsignmentFilter] = useState<ConsignmentFilter>("ALL")
  // The page owns the type chips (one header row with the tabs, as in de-network-admin); standalone use keeps local state.
  const consignmentFilter = consignmentFilterProp ?? internalConsignmentFilter
  const setConsignmentFilter = setConsignmentFilterProp ?? setInternalConsignmentFilter
  const [sorted, setSorted] = useState<[string, Sort]>(["createdAt", Sort.DESC])
  const isSplit = mode === "split"
  const [extraColumns, setExtraColumns] = useState<string[]>([])
  const { filters, setField, reset } = useSupplierSalesFilters()

  const ORD_DN_CREATE = usePermission("ORD_DN_CREATE")
  const { stateOf } = useSalesActions()
  const orderTypes = useSelector((s: { general: IGeneral }) => s.general.orderTypes)

  const visibleColumns = useMemo(
    () => SALES_COLUMNS.filter((c) => (isSplit ? SPLIT_COLUMN_IDS : DEFAULT_COLUMN_IDS).includes(c.id) || extraColumns.includes(c.id)),
    [extraColumns, isSplit],
  )
  const cellCtx = useMemo(
    () => ({ typeName: (code?: string | null) => orderTypes?.find((o: { code: string; name?: string }) => o?.code === code)?.name }),
    [orderTypes],
  )

  const reload = useCallback(() => { tableRef.current?.reload() }, [])

  // The split tab sends only the filters de-order-service order/split/list.ts accepts.
  const requestFilters = useMemo(() => {
    if (!isSplit) return filters
    const { businessId, salesCode, createdAt, orderStatus } = filters
    return { businessId: businessId ?? null, salesCode: salesCode ?? "", createdAt: createdAt ?? [null, null], orderStatus: orderStatus ?? null, type: null }
  }, [filters, isSplit])

  useImperativeHandle(ref, () => ({
    getFilters: () => requestFilters as unknown as typeof filters,
    getOrder: () => sorted,
    reload,
  }), [requestFilters, sorted, reload])

  const onSort = useCallback((index: string) => {
    setSorted((state) => {
      const [currentIndex, currentOrder] = state
      let newOrder = Sort.ASC
      if (currentIndex === index) newOrder = currentOrder === Sort.ASC ? Sort.DESC : Sort.ASC
      return [index, newOrder]
    })
  }, [])

  const onClear = useCallback(() => {
    reset()
    setConsignmentFilter("ALL")
  }, [reset])

  const onToggleSelect = useCallback((row: IOrderPlus) => {
    if (checked.some((item) => item.id === row.id)) {
      setChecked(checked.filter((item) => item.id !== row.id))
    } else {
      setChecked([...checked, row])
    }
  }, [checked, setChecked])

  const onAction = useCallback((key: RowActionKey, record: IOrderPlus) => {
    switch (key) {
      case "edit":
        navigate(`/supplier-sales/edit/${record?.id}/${record?.receiverBusinessId}`)
        return
      case "detail":
        navigate(`/supplier-sales/detail/${record?.id}`)
        return
      case "cancel":
        setAction({ kind: "cancel", record })
        return
      case "reviewed":
        setAction({ kind: "reviewed", record })
        return
      case "confirm":
        setAction({ kind: "confirm", record, constant: "confirm" })
        return
      case "reject":
        setAction({ kind: "reject", record, constant: "reject" })
        return
      case "backorder":
        setActions(["backorder", [record] as never])
        return
      default: {
        const _exhaustive: never = key
        return _exhaustive
      }
    }
  }, [navigate, setActions])

  const selectableRows = useMemo(() => currentRows.filter(isSelectable), [currentRows])
  const checkedSelectable = useMemo(() => (checked || []).filter(isSelectable), [checked])
  const allSelected = selectableRows.length > 0 && checkedSelectable.length === selectableRows.length

  // Help panel context (@dehub/help): how many rows can get a delivery, how many wait for the receiver.
  const { set: setHelpContext } = useHelpContext()
  const waitingCount = useMemo(() => currentRows.filter((r) => r.orderStatus === "REVIEWED").length, [currentRows])
  useEffect(() => {
    setHelpContext({ vars: { eligibleCount: selectableRows.length, waitingCount } })
  }, [selectableRows.length, waitingCount, setHelpContext])

  const tableFilters = useMemo<Record<string, string | number | null | (string | number | Date | null)[]>>(() => ({
    ...requestFilters,
    ...(isSplit || consignmentFilter === "ALL"
      ? {}
      : consignmentFilter === "VMI_CRON"
        ? { source: "VMI_CRON" }
        : consignmentFilter === "CONSIGNMENT"
          ? { consignmentClassification: "CONSIGNMENT" }
          : { consignmentClassification: "REGULAR" }),
  }), [requestFilters, consignmentFilter, isSplit])

  // Cancelling covers only the table: the filters and tabs stay usable (Ord_States).
  const [busy, setBusy] = useState(false)
  const cancelOrder = async (record: IOrderPlus) => {
    setBusy(true)
    try {
      await OrderApi.cancel(record.id!)
      message.success(t("supplierSales:sales.cancelSuccess"))
      setAction({ kind: "none" })
      reload()
    } catch (err) {
      message.error((err as HttpHandler)?.message!)
    } finally {
      setBusy(false)
    }
  }

  const stateOfRow = useCallback((a: SalesAction, o: Parameters<typeof stateOf>[1]) => stateOf(a, o), [stateOf])

  return (
    <>
      <Flex direction="column" gap="md" mt="md">
        <SupplierSalesFilterBar
          filters={filters}
          setField={setField}
          consignmentFilter={consignmentFilter}
          setConsignmentFilter={setConsignmentFilter}
          onClear={onClear}
          mode={mode}
          visibleColumns={extraColumns}
          setVisibleColumns={setExtraColumns}
        />

        <Box pos="relative">
          <LoadingOverlay visible={busy} />
          <CustomsTable
            onResult={(thisTableData: { count: number; rows: IOrderPlus[] }) => {
              setCurrentRows(thisTableData?.rows ?? [])
            }}
            ref={tableRef}
            sorted={sorted}
            emptyTitle={t("supplierSales:sales.list.emptyTitle")}
            // Content-driven empty state (spec 10 §2.10): type chips get their own variant (VMI_CRON / CONSIGNMENT).
            renderEmpty={(state) => state === "empty" ? <EmptyStateGuide helpKey="order.supplier-sales-list.empty.main" /> : consignmentFilter === "VMI_CRON" || consignmentFilter === "CONSIGNMENT" ? <EmptyStateGuide helpKey="order.supplier-sales-list.empty.main" when={consignmentFilter} /> : null}
            emptyAction={onCreate ? { label: t("supplierSales:sales.list.emptyAction"), onClick: onCreate } : undefined}
            onClearFilters={onClear}
            thead={
              <SupplierSalesTableHead
                columns={visibleColumns}
                sorted={sorted}
                onSort={onSort}
                showSelect={ORD_DN_CREATE.hasAccess}
                allSelected={allSelected}
                someSelected={checkedSelectable.length > 0}
                selectableCount={selectableRows.length}
                onSelectAll={() => setChecked(selectableRows)}
                onUnselectAll={() => setChecked([])}
              />
            }
            tbody={(rows: IOrderPlus[]) => (
              <Mantinetable.Tbody>
                {(rows || []).map((record, index) => (
                  <SupplierSalesTableRow
                    key={record.id ?? index}
                    record={record}
                    columns={visibleColumns}
                    ctx={cellCtx}
                    isChecked={checked.some((item) => item.id === record.id)}
                    isSelectable={isSelectable(record)}
                    showSelect={ORD_DN_CREATE.hasAccess}
                    onToggleSelect={onToggleSelect}
                    onAction={onAction}
                    stateOf={stateOfRow}
                    detailOnly={isSplit}
                  />
                ))}
              </Mantinetable.Tbody>
            )}
            name={isSplit ? "supplier-split.list" : "supplier-sales.list"}
            filters={tableFilters}
            loadData={(data) => (isSplit ? SplitApi.list(data!) : OrderApi.list(data!))}
          />
        </Box>
      </Flex>

      {!isSplit && <SupplierSalesActionModals
        action={action}
        setAction={setAction}
        parentActions={actions}
        setParentActions={setActions}
        reload={reload}
        setChecked={setChecked}
        checked={checked}
        onConfirmCancel={cancelOrder}
        cancelBusy={busy}
      />}
    </>
  )
}

export const SupplierSalesList = forwardRef(Component)
